import React, { useState, useEffect, useRef } from "react"
import Fuse from 'fuse.js'
import fuzzysort from 'fuzzysort'
import pluralize from 'pluralize'


const compact = (arr) => {
  return arr.filter(x => x !== null && x !== undefined && x !== '')
}
const simple_colors = [
  'red', 'green', 'blue', 'brown', 'tan', 
  'beige', 'offwhite', 'natural', 'white', 
  'black', 'grey', 'gray', 'navy', 'yellow',
  'orange', 'purple'
]


const useSearchHook = ({searchType, currSort='best', currSizes=[], setSuggests=false, plstest=false, dontGuess}) => {
  const [didLoad, setDidLoad] = useState(false)
  const [currVals, setCurrVals] = useState([])
  const [prodsJson, setProdsJson] = useState([])
  const [boost, setBoost] = useState([])
  const [synonyms, setSynonyms] = useState([])


  const gendProds = useRef([])
  const fuzzIndex = useRef([])
  const fuzzTags = useRef({})

  useEffect(() => {
    if (!didLoad) return
    setupFuzz(prodsJson)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType])


  // ----------------------------------------------------------------
  // External -------------------------------------------------------
  // ----------------------------------------------------------------


  const getSearchData = async (coll_ids=[]) => {
    const is_stag = process.env.GATSBY_CURR_ENV === 'development'
    let url = is_stag 
      ? 'https://cdn-staging.buckmason.com/search/search-json.json' 
      : 'https://cdn.buckmason.com/search/search-json.json'
    // url = 'https://s3.us-west-1.amazonaws.com/mokah-livestaging/search/search-json.json'

    const res = await fetch(url)
    let data = await res.json()
    let products = uncompData(data)
    if (coll_ids.length > 0) products = compact(coll_ids.map(id => products.find(x => x.mid === id)))

    setProdsJson(products)
    setupFuzz(products)
    if (data.boost.length > 0) setBoost(data.boost)
    if (data.synonyms.length > 0) setSynonyms(data.synonyms)
    setDidLoad(true)
    
    return {products: null, callouts: data.callouts}
  }


  const buildVals = (val) => {
    const gends = ['men', 'mens', "men's", 'women', 'womens', "women's"]
    let val_arr = compact(val.trim().split(' ')
                    .map(x => x.trim()))
                    .filter(x => !gends.includes(x))
    let avail_tags = [...new Set( Object.keys(fuzzTags.current).flatMap(x => fuzzTags.current[x]) )]
    avail_tags = compact(avail_tags)
    return val_arr.map(x => ({term: x, match: avail_tags.includes(x)}))
  }


  const searchProds = ({val}) => {
    let val_arr = buildVals(val)
    if (val_arr.length < 1) return null

    let terms = []
    let is_guess = false
    val_arr.forEach(item => {
      if (item.match) {
        terms.push(item.term)
        return
      }
      let guesses = nfuzzVals(item.term, terms)
      if (guesses.some(x => x !== item.term)) {
        is_guess = true
        terms = terms.concat(guesses)
      }
    })

    let sug_terms = terms.map(x => x + '')
    if (dontGuess) {
      is_guess = true
      terms = val_arr.map(x => x.term)
    }

    let newProds = fuzzSearch({val, vals: terms})
    let results = postSearch({newProds, val, terms})

    if (results.flat().length < 1 && !dontGuess) {
      is_guess = false
      sug_terms = []
    }
    if (!!setSuggests) setSuggests(is_guess ? sug_terms : [])

    return results
  }


  const searchSku = (sku) => {
    let plProds = prodsJson.filter(x => x.sku?.toLowerCase()?.includes(sku))
    plProds = plGroup({plProds, products: prodsJson, vals: ['tall','black']})
    return plProds
  }


  const filterSizes = ({results, val}) => {
    let new_results
    const vals = val.split(' ')
    const has_color = vals.some(term => simple_colors.includes(term) || simple_colors.includes(term.slice(0,-1)))

    if (results[0].hasOwnProperty('feats')) {
      new_results = results.map(pline => {
        let feats = pline.feats.filter(product => {
          let sizes = compact(Object.keys(product.variants).map(k => !!product.variants[k] ? k : null))
          return currSizes.some(x => sizes.includes(x))
        })
        let products = pline.products.filter(product => {
          let sizes = compact(Object.keys(product.variants).map(k => !!product.variants[k] ? k : null))
          return currSizes.some(x => sizes.includes(x))
        })
        if (!has_color) {
          feats = feats.concat(products)
          products = []
        }

        return {...pline, feats, products}
      })
      new_results = new_results.filter(x => x.feats.length > 0)
    } else {
      new_results = results.flat().filter(product => {
        let sizes = compact(Object.keys(product.variants).map(k => !!product.variants[k] ? k : null))
        return currSizes.some(x => sizes.includes(x))
      })
      new_results = [new_results]
    }

    return new_results
  }


  const saveSearch = (obj, vals) => {
    obj['val_arr'] = vals
    localStorage.setItem('search', JSON.stringify(obj))

    return true
  }
  

  const getSearch = () => {
    if (!localStorage.getItem('search')) return false
    const obj = JSON.parse(localStorage.getItem('search'))
    return obj
  }


  // ----------------------------------------------------------------
  // Setup ----------------------------------------------------------
  // ----------------------------------------------------------------


  const setupFuzz = (products) => {
    let tosearch = products
    const gends = [searchType, 'u']
    if (searchType) tosearch = tosearch.filter(x => gends.includes(x.gender))

    let fuzzprods = tosearch.map(product => {
      const is_gift = product.handle.includes('gift')
      const sizes_key = {
        'XXS': 'extra extra small',
        'XS': 'extra small', 
        'S': 'small', 
        'M': 'medium', 
        'L': 'large', 
        'XL': 'extra large', 
        'XXL': 'extra extra large', 
        'OS': 'one size'
      }
      if (!is_gift && !!product.variants) {
        let needs_text = Object.keys(sizes_key).some(x => product.search.sizes.includes(x))
        if (needs_text) {
          product.search.sizes = compact(product.search.sizes.concat(product.search.sizes.map(x => sizes_key[x])))
        }
      }
      synonyms.forEach(arr => {
        const found = arr.some(x => product.search.type.includes(x)) || arr.some(x => product.search.name.includes(x))
        if (found) {
          product.search.type = [...new Set(product.search.type.concat(arr) )]
        }
      })

      return {
        id: product.mid,
        gender: product.gender,
        ...product.search
      }
    })
    gendProds.current = tosearch
    fuzzIndex.current = fuzzprods

    const prodTags = {
      colors: [...new Set( fuzzprods.flatMap(x => x.color) )],
      types: [...new Set( fuzzprods.flatMap(x => x.type) )],
      names: [...new Set( fuzzprods.flatMap(x => x.name) )],
      fits: [...new Set( fuzzprods.flatMap(x => x.fit) )],
      fabrics: [...new Set( fuzzprods.flatMap(x => x.fabric) )],
      sizes: [...new Set( fuzzprods.flatMap(x => x.sizes) )]
    }
    fuzzTags.current = prodTags
  }


  const uncompData = (data) => {
    const tags = data.tags
    let prods = data.products.map(prod => {
      const newobj = {
        mid: prod.mid,
        pl_id: prod.pid,
        shop_id: prod.sid,
        handle: prod.h,
        title: prod.t,
        pub: prod.pb,
        pl_name: prod.pn,
        color: prod.c,
        gender: prod.g,
        pl_colors: prod.pc,
        media: prod.m,
        available: !!prod.a,
        price: prod.pr,
        best: prod.be,
        broken: !!prod.br,
        sku: prod.sk,
        callouts: prod.ca || [],
        isalt: !!prod.isa,
        altcuts: prod.ac,
        variants: prod.v,
        search: prod.sch
      }
      newobj.handle = newobj.handle.map(x => tags[x]).join('-')
      newobj.title = newobj.title.map(x => tags[x]).join(' ')
      newobj.pl_name = newobj.pl_name.map(x => tags[x]).join(' ')
      let sizes = []
      if (!newobj.handle.includes('gift') && !!newobj.variants) {
        sizes = compact(Object.keys(newobj.variants).map(k => !!newobj.variants[k] ? k : null))
      }
      newobj.sizes = sizes

      if (prod.sch) {
        let search = prod.sch
        Object.keys(search).forEach(k => 
          search[k] = search[k].map(x => tags[x])
        )
        newobj.search = search
      }

      return newobj
    })
    return prods
  }


  // ----------------------------------------------------------------
  // Searching ------------------------------------------------------
  // ----------------------------------------------------------------


  const fuzzSearch = ({val, vals}) => {
    const tosearch = fuzzIndex.current.map(prod => (
      {
        ...prod,
        type: prod.type.join(),
        name: prod.name.join(),
        color: prod.color.join(),
        fit: prod.fit.join(),
        fabric: prod.fabric.join(),
        sizes: prod.sizes.join()
      }
    ))
    
    let results = fuzzysort.go(vals.join(' '), tosearch, {
      keys: ['type', 'name', 'color', 'fit', 'fabric', 'sizes'], 
      threshold: -10000
    })
    results = results.filter(x => x._score >= -10000)

    let newProds = results.map(res => gendProds.current.find(x => x.mid === res.obj.id) )
    newProds = cleanResults({newProds, val, vals})

    return newProds
  }


  const nfuzzVals = (queryVal, terms) => {
    if (simple_colors.includes(queryVal)) return queryVal

    const tags = JSON.parse(JSON.stringify(fuzzTags.current))
    tags.colors = [...new Set(fuzzTags.current.colors.flatMap(x => x.includes('/') ? x.split('/') : x))]

    let colors = compact(findGuess({queryVal, arr: tags.colors}))
    let others = Object.keys(tags)
                  .filter(x => x !== 'colors')
                  .map(x => findGuess({queryVal, arr: tags[x], colors}))
    others = others.filter(x => compact(x).length > 0)
    let guesses = colors.concat(compact(others.flat()))
                        .filter(x => !terms.includes(x))
    guesses = [...new Set(compact(guesses))]

    let fuse = new Fuse(guesses.sort((a,b) => a.length - b.length).map(tag => ({ tag })), {
      threshold: 0.3,
      ignoreDiacritics: true,
      includeScore: true,
      keys: ['tag']
    })
    const best = fuse.search(queryVal).map(x => x.item.tag)
    const substrs = checkForSubs(queryVal, guesses)

    const inbest = best.length > 0 && substrs.length > 0 && best[0].includes(queryVal)

    if ((best.length > 0 && substrs.length < 1) || inbest) return [best[0]]
    if (substrs.length > 0) return substrs.slice(0,2)

    return guesses.slice(0,1)
  }


  const findGuess = ({queryVal, arr, colors=[]}) => {
    if (queryVal.length < 3) return [queryVal]

    let found = []
    const substr = arr.filter(x => queryVal.includes(x) || x.includes(queryVal))
    if (substr.length > 0) found = found.concat(substr)

    const objs = arr.map(tag => ({tag}))
    let fuzzguess = fuzzysort.go(queryVal, objs, {key: ['tag'],threshold: -10000})
    if (fuzzguess.length > 0) found = found.concat(fuzzguess.map(x => x.target))

    let tagFuse = new Fuse(arr.map(tag => ({ tag })), {
      threshold: 0.5,
      ignoreDiacritics: true,
      includeScore: true,
      keys: ['tag']
    })

    const fuseguess = fuseCheck({queryVal, tagFuse})
    if (fuseguess.length > 0) found = found.concat(fuseguess)

    return found
  }


  function fuseCheck({queryVal, tagFuse}) {
    const subfound = new Map()
    for (let start = 0; start < queryVal.length; start++) {
      for (let len = 3; len <= 7 && start + len <= queryVal.length; len++) {
        const slice = queryVal.slice(start, start + len);
        const result = tagFuse.search(`'${slice}`)?.[0];
        if (result && result.score < 0.5) {
          const existingScore = subfound.get(result.item.tag);
          if (existingScore === undefined || result.score < existingScore) {
            subfound.set(result.item.tag, result.score);
          }
        }
      }
    }
    return [...subfound.entries()]
      .sort((a, b) => {
        const scoreCompare = a[1] - b[1]
        if (scoreCompare !== 0) return scoreCompare
        return a[0].length - b[0].length
      })
      .map(([tag]) => tag)
  }


  function checkForSubs(queryVal, guesses) {
    const minLen = 3
    const maxLen = 7
    const scoreThreshold = 0.4 
    const found = new Map()

    const tagList = guesses.filter(x => x.length < queryVal.length)

    let fuse = new Fuse(tagList.map(tag => ({ tag })), {
      threshold: 0.3,
      distance: 20, 
      ignoreLocation: false,
      location: 0,
      includeScore: true,
      keys: ['tag']
    })

    for (let start = 0; start < queryVal.length; start++) {
      for (let len = minLen; len <= maxLen && start + len <= queryVal.length; len++) {
        const slice = queryVal.slice(start, start + len)
        const result = fuse.search(slice)?.[0]

        if (result && result.score < scoreThreshold) {
          const matchedTag = result.item.tag;
          const positionBonus = start === 0 ? 0.1 : 0
          const adjustedScore = result.score - positionBonus

          const existingScore = found.get(matchedTag)
          if (existingScore === undefined || adjustedScore < existingScore) {
            found.set(matchedTag, adjustedScore)
          }
        }
      }
    }
    return [...found.entries()]
      .sort((a, b) => {
        const scoreCompare = a[1] - b[1]
        if (scoreCompare !== 0) return scoreCompare
        return a[0].length - b[0].length
      })
      .map(([tag]) => tag)
  }



  // ----------------------------------------------------------------
  // Post Search ----------------------------------------------------
  // ----------------------------------------------------------------


  const cleanResults = ({newProds, val, vals}) => {
    let results = newProds

    if (val.includes('shorts') && (!val.includes('slee') && !val.includes('skirt'))) {
      const shorts_skips = ['tee','shirt','teeshirt','tshirt','sleeve','crew']
      results = results.filter(res => {
        const tags = [...res.search.type,...res.search.name,...res.search.fit]
        return !tags.some(x => shorts_skips.includes(x))
      })
    }
    if (vals.includes('shirt') && !val.includes('tee')) {
      const shorts_skips = ['tee','tees','sweat']
      results = results.filter(res => {
        const tags = [...res.search.type,...res.search.name,...res.search.fit]
        return !tags.some(x => shorts_skips.includes(x))
      })
    }
    if ((vals.includes('suit') || vals.includes('suite') || vals.includes('suiting')) && !val.includes('swim')) {
      const suit_skips = ['shirting']
      results = results.filter(res => {
        const tags = [...res.search.type,...res.search.name,...res.search.fit]
        return !tags.some(x => suit_skips.includes(x))
      })
    }
    if (val.includes('shorts') && (!val.includes('slee') && !val.includes('skirt'))) {
      results = results.filter(x => !x.handle.includes('slim-crew') && !x.handle.includes('easy-crew'))
    }
    return results
  }


  const postSearch = ({newProds, val, terms}) => {
    let boosts = checkBoost({newProds, boost})
    newProds = boosts.prods
    boosts = boosts.found

    let results
    if (currSort !== 'asc') {
      results = sortProds(newProds)
    } else {
      results = sortProds(boosts.concat(newProds))
    }

    if (plstest) {
      let plProds = results
      if (currSort !== 'asc') plProds = boosts.concat(newProds)
      if (val.includes('suit') && currSort === 'best' && (!val.includes('swim') && !val.includes('bath'))) {
        plProds = sortSuitJackets(plProds).flat()
      }
      plProds = plGroup({plProds, products: gendProds.current, vals: terms})
      return plProds
    }
    if (currSort === 'asc') return [results]

    results = [newProds]
    if (val.includes('suit') && currSort === 'best' && (!val.includes('swim') && !val.includes('bath'))) {
      results = sortSuitJackets(results)
    }

    if (boosts.length > 0) results.unshift(boosts)

    return results
  }

  
  const checkBoost = ({newProds, boost}) => {
    const fallback = {found: [], prods: newProds}

    if (newProds.length < 1) return fallback
    const any_boost = boost.some(id => newProds.find(x => x.mid === id))
    if (!any_boost) return fallback

    let found = newProds.filter(x => boost.includes(x.mid))
    let reg_prods = newProds.filter(x => !boost.includes(x.mid))
    found = found.sort((a, b) => boost.indexOf(a.mid) - boost.indexOf(b.mid))

    return {found, prods: reg_prods}
  }


  const plGroup = ({plProds, products, vals}) => {
    const has_color = vals.some(term => simple_colors.includes(term) || simple_colors.includes(term.slice(0,-1)))
    const alt_q = vals.includes('short') || vals.includes('tall')

    let prods = plProds.flat()
    const pl_ids = [...new Set(prods.map(x => x.pl_id))]

    let pl_groups = pl_ids.map(id => {
      let feats = prods.filter(x => x.pl_id === id)
      if (feats.length < 1) return null
      if (plstest !== 'pos' && !has_color && !(currSizes?.length > 0)) feats = feats.slice(0,1)

      const feat_ids = feats.map(x => x.mid)
      const pl_prods = products.filter(x => x.pl_id === id && !feat_ids.includes(x.mid))
      const is_alt = feats.find(x => x.isalt) || pl_prods.find(x => x.isalt)

      let price
      if (feats[0].handle.includes('gift')) {
        price = `25-$1,000`
      } else {
        price = feats[0].price
      }

      return {
        pl_id: id,
        pl_name: feats[0].pl_name,
        is_alt: is_alt,
        gender: feats[0].gender,
        price: price,
        feats: feats,
        products: pl_prods
      }
    })
    pl_groups = compact(pl_groups)
    if (!alt_q) pl_groups = pl_groups.filter(x => !x.is_alt)

    return pl_groups
  }


  const sortSuitJackets = (results) => {
    let nresults = results.flat()
    let jackets = nresults.filter(x => x.search.type.includes('jacket') || x.search.name.includes('jackets'))
    let nonjacket = nresults.filter(x => !(x.search.type.includes('jacket') || x.search.name.includes('jackets')))

    const suits = jackets.flatMap(jacket => {
      const pant = nonjacket.find(x => {
        const is_color = x.color === jacket.color 
        const ptags = [...x.search.type, ...x.search.name, ...x.search.fit]
        const is_pant = (ptags.includes('pant') || ptags.includes('trouser') || ptags.includes('short'))
        if (!is_color || !is_pant) return false

        let jack_hand = jacket.handle.split('-')
        let jack_col = jacket.color.split(' ')
        jack_hand = jack_hand.filter(term => !jack_col.includes(term))

        let pant_hand = x.handle.split('-')
        let pant_col = x.color.split(' ')
        pant_hand = pant_hand.filter(term => !pant_col.includes(term))

        const is_same = jack_hand.some(i => pant_hand.includes(i))
        return is_color && is_pant && is_same
      })
      if (!pant) return [jacket]
      return [jacket, pant]
    })
    const suit_ids = suits.map(x => x.mid)
    jackets = jackets.filter(x => !suit_ids.includes(x.mid))
    nonjacket = nonjacket.filter(x => !suit_ids.includes(x.mid))

    nresults = [...suits,...jackets,...nonjacket]

    return [nresults]
  }


  const sortProds = (products, useHits=false) => {
    let prods = products

    if (currSort === 'rec') {
      if (!useHits) {
        prods = prods.sort((a, b) => {
          const a_avails = a.sizes.length > 6 ? 6 : a.sizes.length
          const b_avails = b.sizes.length > 6 ? 6 : b.sizes.length
          if (a.broken !== b.broken) return a.broken - b.broken
          if (a_avails !== b_avails) return b_avails - a_avails
          return b.pub - a.pub
        })
      } else {
        prods = prods.sort((a, b) => {
          if (a.hits !== b.hits) return b.hits - a.hits
          if (a.broken !== b.broken) return a.broken - b.broken 
          return b.pub - a.pub
        })
      }
    } else if (currSort === 'best') {
      if (!useHits) {
        prods = prods.sort((a, b) => {
          const a_avails = a.sizes.length > 6 ? 6 : a.sizes.length
          const b_avails = b.sizes.length > 6 ? 6 : b.sizes.length
          if (a.broken !== b.broken) return a.broken - b.broken
          if (a_avails !== b_avails) return b_avails - a_avails
          return a.best - b.best
        })
      } else {
        prods = prods.sort((a, b) => {
          if (a.broken !== b.broken) return a.broken - b.broken 
          return a.best - b.best
        })
      }
    } else {
      prods = prods.sort((a, b) => a.price - b.price)
    }
    
    return prods
  }



  return { getSearchData, buildVals, searchProds, filterSizes, saveSearch, getSearch, searchSku}
}










export default useSearchHook
